<template>

    <div v-if="Object.keys(successNotifications).length > 0" class="my-3">
        <div class="alert alert-success alert-dismissible d-flex justify-content-center" role="alert">
            <ul v-if="Object.keys(successNotifications).length > 1" class="my-0">
                <li v-for="(msg, index) in successNotifications" :key="index">{{msg}}</li>
            </ul>
            <!-- when there is just 1 message to display -->
            <span v-else class="" v-html="Object.values(successNotifications)[0]"></span>
            <button type="button" class="btn-close" @click="emit('dismiss', 'success')"></button>
        </div>
    </div>

    <div v-if="Object.keys(warningNotifications).length > 0" class="my-3">
        <div class="alert alert-warning alert-dismissible d-flex justify-content-center" role="alert">
            <ul v-if="Object.keys(warningNotifications).length > 1" class="my-0">
                <li v-for="(msg, index) in warningNotifications" :key="index">{{msg}}</li>
            </ul>
            <!-- when there is just 1 message to display -->
            <span v-else class="" v-html="Object.values(warningNotifications)[0]"></span>
            <button type="button" class="btn-close" @click="emit('dismiss', 'warning')"></button>
        </div>
    </div>

    <div v-if="Object.keys(errorNotifications).length > 0" class="my-3">
        <div class="alert alert-warning alert-dismissible d-flex justify-content-center" role="alert">
            <ul v-if="Object.keys(errorNotifications).length > 1" class="my-0">
                <li v-for="(msg, index) in errorNotifications" :key="index">{{msg}}</li>
            </ul>
            <!-- when there is just 1 message to display -->
            <span v-else class="" v-html="Object.values(errorNotifications)[0]"></span>
            <button type="button" class="btn-close" @click="emit('dismiss', 'error')"></button>
        </div>
    </div>

</template>

<script setup>

import { computed, watch, onBeforeUnmount } from 'vue';

const emit = defineEmits(['dismiss']);

const props = defineProps({
    notifications: {
        type: Object,
        default: null,
    },
});

const successNotifications = computed(() => getVisibleMessages(props.notifications.success));
const errorNotifications = computed(() => getVisibleMessages(props.notifications.error));
const warningNotifications = computed(() => getVisibleMessages(props.notifications.warning));

function getVisibleMessages(notificationObj) {
    if (!notificationObj) {
        return {};
    }
    const messages = {};
    for (const [key, value] of Object.entries(notificationObj)) {
        if (!key.startsWith('_')) {
            messages[key] = value;
        }
    }
    return messages;
}

function getAutoDismissSeconds(notificationObj) {
    return notificationObj?._autoDismissSeconds ?? 0;
}

const timers = {};

function scheduleAutoDismiss(type, notificationObj) {
    clearTimer(type);
    const seconds = getAutoDismissSeconds(notificationObj);
    if (seconds > 0) {
        timers[type] = setTimeout(() => {
            emit('dismiss', type);
        }, seconds * 1000);
    }
}

function clearTimer(type) {
    if (timers[type]) {
        clearTimeout(timers[type]);
        delete timers[type];
    }
}

watch(() => props.notifications.success, (val) => {
    if (Object.keys(getVisibleMessages(val)).length > 0) {
        scheduleAutoDismiss('success', val);
    } else {
        clearTimer('success');
    }
}, { deep: true });

watch(() => props.notifications.warning, (val) => {
    if (Object.keys(getVisibleMessages(val)).length > 0) {
        scheduleAutoDismiss('warning', val);
    } else {
        clearTimer('warning');
    }
}, { deep: true });

watch(() => props.notifications.error, (val) => {
    if (Object.keys(getVisibleMessages(val)).length > 0) {
        scheduleAutoDismiss('error', val);
    } else {
        clearTimer('error');
    }
}, { deep: true });

onBeforeUnmount(() => {
    Object.keys(timers).forEach(clearTimer);
});

</script>
